import { DateTime } from "luxon";
import { useMemo } from "react";
import { useRegisterViewContext } from "@/domains/ai/ai-chat/hooks/use-register-view-context";
import type { OperationsData, OverviewData, WebData } from "../types";

interface UseReportsViewContextProps {
  key: string;
  currentPeriod: { startDate: Date; endDate: Date } | null;
  comparisonPeriod: { startDate: Date; endDate: Date } | null;
  overviewData: OverviewData | null;
  operationsData?: OperationsData | null;
  webData?: WebData | null;
  loading: boolean;
  isLimitedAccess: boolean;
}

export function useReportsViewContext({
  key,
  currentPeriod,
  comparisonPeriod,
  overviewData,
  loading,
  isLimitedAccess,
}: UseReportsViewContextProps) {
  const shortContext = useMemo(() => {
    if (!currentPeriod) return "";

    const startDate = DateTime.fromJSDate(currentPeriod.startDate);
    const endDate = DateTime.fromJSDate(currentPeriod.endDate);
    const dateRange = `${startDate.toFormat("MMM dd")} - ${endDate.toFormat("MMM dd, yyyy")}`;

    // Loading state
    if (loading) {
      return `📊 Reports: ${dateRange} | Loading data...`;
    }

    // Access level with clear premium indication
    const accessLevel = isLimitedAccess
      ? "Basic tier (limited metrics)"
      : "Premium (full analytics)";

    // Count available metrics
    let metricsCount = 0;
    if (overviewData) {
      metricsCount += Object.keys(overviewData.financial).length - 2; // -2 for non-metric fields
      metricsCount += Object.keys(overviewData.customers).length - 3; // -3 for non-metric fields
      metricsCount += Object.keys(overviewData.bookings).length;
    }

    // Data availability status
    const dataStatus = overviewData ? `${metricsCount} metrics` : "no data";

    return `📊 Reports: ${dateRange} | ${accessLevel} | ${dataStatus}`;
  }, [currentPeriod, isLimitedAccess, overviewData, loading]);

  const longContext = useMemo(() => {
    if (!currentPeriod) {
      return "No report period selected";
    }

    const startDate = DateTime.fromJSDate(currentPeriod.startDate);
    const endDate = DateTime.fromJSDate(currentPeriod.endDate);
    const dateRange = `${startDate.toFormat("MMM dd")} - ${endDate.toFormat("MMM dd, yyyy")}`;

    // Loading state
    if (loading) {
      return `
📊 Reports Dashboard
📆 Period: ${dateRange}
⏳ Status: Loading data...

Please wait while report data is being fetched.
      `.trim();
    }

    // No data available
    if (!overviewData) {
      return `
📊 Reports Dashboard
📆 Period: ${dateRange}
❌ Status: No data available

Report data could not be loaded for this period.
      `.trim();
    }

    const comparisonStr = comparisonPeriod
      ? `vs ${DateTime.fromJSDate(comparisonPeriod.startDate).toFormat("MMM dd")} - ${DateTime.fromJSDate(comparisonPeriod.endDate).toFormat("MMM dd, yyyy")}`
      : "";

    // Format financial metrics (values are in cents, convert to dollars)
    const financialMetrics: string[] = [];
    if (overviewData.financial.totalRevenue) {
      const { current, changePercent } = overviewData.financial.totalRevenue.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      financialMetrics.push(`- Total Revenue: $${(current / 100).toFixed(2)}${changeStr}`);
    }
    if (overviewData.financial.averageTransactionValue) {
      const { current, changePercent } = overviewData.financial.averageTransactionValue.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      financialMetrics.push(
        `- Avg Transaction: $${(current / 100).toFixed(2)}${changeStr} [Premium]`
      );
    } else if (!isLimitedAccess) {
      financialMetrics.push(`- Avg Transaction: (calculating...)`);
    }
    if (overviewData.financial.totalTransactions) {
      const { current, changePercent } = overviewData.financial.totalTransactions.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      financialMetrics.push(`- Total Transactions: ${current}${changeStr} [Premium]`);
    } else if (!isLimitedAccess) {
      financialMetrics.push(`- Total Transactions: (calculating...)`);
    }

    // Format customer metrics
    const customerMetrics: string[] = [];
    if (overviewData.customers.totalCustomers) {
      const { current, changePercent } = overviewData.customers.totalCustomers.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      customerMetrics.push(`- Total Customers: ${current}${changeStr}`);
    }
    if (overviewData.customers.newCustomers) {
      const { current, changePercent } = overviewData.customers.newCustomers.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      customerMetrics.push(`- New Customers: ${current}${changeStr}`);
    }
    if (overviewData.customers.returningCustomers) {
      const { current, changePercent } = overviewData.customers.returningCustomers.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      customerMetrics.push(`- Returning Customers: ${current}${changeStr} [Premium]`);
    } else if (!isLimitedAccess) {
      customerMetrics.push(`- Returning Customers: (calculating...)`);
    }

    // Format booking metrics
    const bookingMetrics: string[] = [];
    if (overviewData.bookings.totalBookings) {
      const { current, changePercent } = overviewData.bookings.totalBookings.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      bookingMetrics.push(`- Total Bookings: ${current}${changeStr}`);
    }
    if (overviewData.bookings.completedBookings) {
      const { current, changePercent } = overviewData.bookings.completedBookings.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      bookingMetrics.push(`- Completed: ${current}${changeStr}`);
    }
    if (overviewData.bookings.cancelledBookings) {
      const { current, changePercent } = overviewData.bookings.cancelledBookings.value;
      const changeStr = changePercent
        ? ` (${changePercent > 0 ? "+" : ""}${changePercent.toFixed(1)}%)`
        : "";
      bookingMetrics.push(`- Cancelled: ${current}${changeStr} [Premium]`);
    } else if (!isLimitedAccess) {
      bookingMetrics.push(`- Cancelled: (calculating...)`);
    }

    // Access tier information with upgrade guidance
    const accessInfo = isLimitedAccess
      ? "\n⚠️ Access Tier: Basic (limited to essential metrics only)\n💡 Upgrade to Premium for full analytics including: Avg Transaction, Total Transactions, Returning Customers, Cancelled Bookings, and more advanced metrics."
      : "\n✨ Access Tier: Premium (full analytics access)\n✓ All metrics available including advanced analytics and detailed breakdowns.";

    return `
📊 Reports Dashboard
📆 Period: ${dateRange}
${comparisonStr ? `📈 Comparison: ${comparisonStr}` : ""}${accessInfo}

💰 Financial Metrics:
${financialMetrics.length > 0 ? financialMetrics.join("\n") : "(no financial data)"}

👥 Customer Metrics:
${customerMetrics.length > 0 ? customerMetrics.join("\n") : "(no customer data)"}

📅 Booking Metrics:
${bookingMetrics.length > 0 ? bookingMetrics.join("\n") : "(no booking data)"}
    `.trim();
  }, [currentPeriod, comparisonPeriod, overviewData, isLimitedAccess, loading]);

  useRegisterViewContext({
    key,
    shortContext,
    longContext,
  });
}
